/* eslint-disable jira/filename-case */

import { defineMessage } from '@atlassian/jira-intl';

const configurationErrorTitle = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.configuration-error-title',
	defaultMessage: "Your demo plan couldn't be created because of a configuration error",
	description: 'Title for demo plan creation configuration errors.',
});

const permissionErrorTitle = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.permission-error-title',
	defaultMessage: "Your demo plan couldn't be created because you’re missing a required permission",
	description: 'Title for demo plan creation permission errors.',
});

const tryAgainErrorTitle = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.try-again-error-title',
	defaultMessage: 'Try to create your demo plan again',
	description: 'Title for unknown demo plan creation errors.',
});

const requiredFieldsConfiguredOnIssueError = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.required-fields-configured-on-issue-error',
	defaultMessage:
		'Your issues have required fields which blocked your demo plan from being created. {readMoreLink}',
	description: 'Description for required fields configured on issue error.',
});

const emptyIssueLinksError = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.empty-issue-links-error',
	defaultMessage:
		"Your site doesn't have issue links configured, which are required to create a demo plan. {readMoreLink}",
	description: 'Description for empty issue links error.',
});

const noBrowseProjectsPermissionError = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.no-browse-projects-permission-error',
	defaultMessage:
		"Your demo plan couldn't be created because you don’t have the <permission-type>Browse Projects</permission-type> permission. {readMoreLink}",
	description: 'Description for missing Browse Projects permissions error.',
});

const noCreateIssuesPermissionError = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.no-create-issues-permission-error',
	defaultMessage:
		"Your demo plan couldn't be created because you don’t have the <permission-type>Create Issues</permission-type> permission. {readMoreLink}",
	description: 'Description for missing Create Issues permissions error.',
});

const noTransitionIssuesPermissionError = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.no-transition-issues-permission-error',
	defaultMessage:
		"Your demo plan couldn't be created because you don’t have the <permission-type>Transition Issues</permission-type> permission. {readMoreLink}",
	description: 'Description for missing Transition Issues permissions error.',
});

const noEditIssuesPermissionError = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.no-edit-issues-permission-error',
	defaultMessage:
		"Your demo plan couldn't be created because you don’t have the <permission-type>Edit Issues</permission-type> permission. {readMoreLink}",
	description: 'Description for missing Edit Issues permissions error.',
});

const watchingDisabledError = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.watching-disabled-error',
	defaultMessage:
		"Your demo plan couldn't be created because you have issue watching disabled. {readMoreLink}",
	description: 'Description for issue watching disabled error.',
});

const unknownExceptionError = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.unknown-exception-error',
	defaultMessage:
		"Your demo plan couldn't be created, but we’re not sure why. If the problem persists, contact Atlassian support. {readMoreLink}",
	description: 'Description for an unknown exception error.',
});

const learnMore = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.learn-more',
	defaultMessage: 'Learn more',
	description: 'Text for Learn more error message link',
});

const contactSupport = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.contact-support',
	defaultMessage: 'Contact Atlassian support',
	description: 'Text for Contact Atlassian support error message link',
});

const requiredFieldsConfiguredOnIssueErrorIssueTermRefresh = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.required-fields-configured-on-issue-error-issue-term-refresh',
	defaultMessage:
		'Your work items have required fields which blocked your demo plan from being created. {readMoreLink}',
	description: 'Description for required fields configured on issue error.',
});

const emptyIssueLinksErrorIssueTermRefresh = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.empty-issue-links-error-issue-term-refresh',
	defaultMessage:
		"Your site doesn't have work item links configured, which are required to create a demo plan. {readMoreLink}",
	description: 'Description for empty issue links error.',
});

const noCreateIssuesPermissionErrorIssueTermRefresh = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.no-create-issues-permission-error-issue-term-refresh',
	defaultMessage:
		"Your demo plan couldn't be created because you don’t have the <permission-type>Create work items</permission-type> permission. {readMoreLink}",
	description: 'Description for missing Create Issues permissions error.',
});

const noTransitionIssuesPermissionErrorIssueTermRefresh = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.no-transition-issues-permission-error-issue-term-refresh',
	defaultMessage:
		"Your demo plan couldn't be created because you don’t have the <permission-type>Transition work items</permission-type> permission. {readMoreLink}",
	description: 'Description for missing Transition Issues permissions error.',
});

const noEditIssuesPermissionErrorIssueTermRefresh = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.no-edit-issues-permission-error-issue-term-refresh',
	defaultMessage:
		"Your demo plan couldn't be created because you don’t have the <permission-type>Edit work items</permission-type> permission. {readMoreLink}",
	description: 'Description for missing Edit Issues permissions error.',
});

const watchingDisabledErrorIssueTermRefresh = defineMessage({
	id: 'portfolio-3-create-sample-plan.common.watching-disabled-error-issue-term-refresh',
	defaultMessage:
		"Your demo plan couldn't be created because you have work item watching disabled. {readMoreLink}",
	description: 'Description for issue watching disabled error.',
});

export const ERROR_REPORTING_PACKAGE = 'jiraPortfolio3CreateSamplePlan';
export const ERROR_REPORTING_TEAM = 'advanced-roadmaps';
const CONTACT_SUPPORT_URL = 'https://support.atlassian.com/contact';

const LEARN_MORE_URL =
	'https://support.atlassian.com/jira-software-cloud/docs/view-a-sample-advanced-roadmaps-plan/';

export const MAX_PLAN_NAME_LENGTH = 255;
export const CREATE_SUCCESS_FLAG_ID = 'samplePlanCreatedSuccessfully';
export const CREATE_ERROR_FLAG_ID = 'samplePlanError';

export const REQUIRED_FIELDS_CONFIGURED_ON_ISSUE = 'REQUIRED_FIELDS_CONFIGURED_ON_ISSUE' as const;
const EMPTY_ISSUE_LINKS = 'EMPTY_ISSUE_LINKS' as const;
const NO_BROWSE_PROJECTS_PERMISSION = 'NO_BROWSE_PROJECTS_PERMISSION' as const;
const NO_CREATE_ISSUES_PERMISSION = 'NO_CREATE_ISSUES_PERMISSION' as const;
const NO_TRANSITION_ISSUES_PERMISSION = 'NO_TRANSITION_ISSUES_PERMISSION' as const;
const NO_EDIT_ISSUES_PERMISSION = 'NO_EDIT_ISSUES_PERMISSION' as const;
const WATCHING_DISABLED = 'WATCHING_DISABLED' as const;
export const UNKNOWN_EXCEPTION = 'UNKNOWN_EXCEPTION' as const;

export const USER_SETTINGS_ERRORS: (
	| 'REQUIRED_FIELDS_CONFIGURED_ON_ISSUE'
	| 'EMPTY_ISSUE_LINKS'
	| 'NO_BROWSE_PROJECTS_PERMISSION'
	| 'NO_CREATE_ISSUES_PERMISSION'
	| 'NO_TRANSITION_ISSUES_PERMISSION'
	| 'NO_EDIT_ISSUES_PERMISSION'
	| 'WATCHING_DISABLED'
)[] = [
	REQUIRED_FIELDS_CONFIGURED_ON_ISSUE,
	EMPTY_ISSUE_LINKS,
	NO_BROWSE_PROJECTS_PERMISSION,
	NO_CREATE_ISSUES_PERMISSION,
	NO_TRANSITION_ISSUES_PERMISSION,
	NO_EDIT_ISSUES_PERMISSION,
	WATCHING_DISABLED,
];

export const ERROR_CODE_TITLE_MAPPING: {
	REQUIRED_FIELDS_CONFIGURED_ON_ISSUE: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	EMPTY_ISSUE_LINKS: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	NO_BROWSE_PROJECTS_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	NO_CREATE_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	NO_TRANSITION_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	NO_EDIT_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	WATCHING_DISABLED: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	UNKNOWN_EXCEPTION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = {
	[REQUIRED_FIELDS_CONFIGURED_ON_ISSUE]: configurationErrorTitle,
	[EMPTY_ISSUE_LINKS]: configurationErrorTitle,
	[NO_BROWSE_PROJECTS_PERMISSION]: permissionErrorTitle,
	[NO_CREATE_ISSUES_PERMISSION]: permissionErrorTitle,
	[NO_TRANSITION_ISSUES_PERMISSION]: permissionErrorTitle,
	[NO_EDIT_ISSUES_PERMISSION]: permissionErrorTitle,
	[WATCHING_DISABLED]: configurationErrorTitle,
	[UNKNOWN_EXCEPTION]: tryAgainErrorTitle,
};

export const ERROR_CODE_DESCRIPTION_MAPPING: {
	readonly REQUIRED_FIELDS_CONFIGURED_ON_ISSUE: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly EMPTY_ISSUE_LINKS: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly NO_BROWSE_PROJECTS_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly NO_CREATE_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly NO_TRANSITION_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly NO_EDIT_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly WATCHING_DISABLED: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly UNKNOWN_EXCEPTION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = {
	[REQUIRED_FIELDS_CONFIGURED_ON_ISSUE]: requiredFieldsConfiguredOnIssueError,
	[EMPTY_ISSUE_LINKS]: emptyIssueLinksError,
	[NO_BROWSE_PROJECTS_PERMISSION]: noBrowseProjectsPermissionError,
	[NO_CREATE_ISSUES_PERMISSION]: noCreateIssuesPermissionError,
	[NO_TRANSITION_ISSUES_PERMISSION]: noTransitionIssuesPermissionError,
	[NO_EDIT_ISSUES_PERMISSION]: noEditIssuesPermissionError,
	[WATCHING_DISABLED]: watchingDisabledError,
	[UNKNOWN_EXCEPTION]: unknownExceptionError,
} as const;

export const ERROR_CODE_DESCRIPTION_MAPPING_ISSUE_TERM_REFRESH: {
	readonly REQUIRED_FIELDS_CONFIGURED_ON_ISSUE: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly EMPTY_ISSUE_LINKS: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly NO_BROWSE_PROJECTS_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly NO_CREATE_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly NO_TRANSITION_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly NO_EDIT_ISSUES_PERMISSION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly WATCHING_DISABLED: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	readonly UNKNOWN_EXCEPTION: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = {
	[REQUIRED_FIELDS_CONFIGURED_ON_ISSUE]: requiredFieldsConfiguredOnIssueErrorIssueTermRefresh,
	[EMPTY_ISSUE_LINKS]: emptyIssueLinksErrorIssueTermRefresh,
	[NO_BROWSE_PROJECTS_PERMISSION]: noBrowseProjectsPermissionError,
	[NO_CREATE_ISSUES_PERMISSION]: noCreateIssuesPermissionErrorIssueTermRefresh,
	[NO_TRANSITION_ISSUES_PERMISSION]: noTransitionIssuesPermissionErrorIssueTermRefresh,
	[NO_EDIT_ISSUES_PERMISSION]: noEditIssuesPermissionErrorIssueTermRefresh,
	[WATCHING_DISABLED]: watchingDisabledErrorIssueTermRefresh,
	[UNKNOWN_EXCEPTION]: unknownExceptionError,
} as const;

export const ERROR_LINK_MAPPING: {
	REQUIRED_FIELDS_CONFIGURED_ON_ISSUE: {
		text: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		href: string;
	};
	EMPTY_ISSUE_LINKS: {
		text: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		href: string;
	};
	NO_BROWSE_PROJECTS_PERMISSION: {
		text: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		href: string;
	};
	NO_CREATE_ISSUES_PERMISSION: {
		text: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		href: string;
	};
	NO_TRANSITION_ISSUES_PERMISSION: {
		text: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		href: string;
	};
	NO_EDIT_ISSUES_PERMISSION: {
		text: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		href: string;
	};
	WATCHING_DISABLED: {
		text: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		href: string;
	};
	UNKNOWN_EXCEPTION: {
		text: {
			id: string;
			defaultMessage: string;
			description: string;
		};
		href: string;
	};
} = {
	[REQUIRED_FIELDS_CONFIGURED_ON_ISSUE]: { text: learnMore, href: LEARN_MORE_URL },
	[EMPTY_ISSUE_LINKS]: { text: learnMore, href: LEARN_MORE_URL },
	[NO_BROWSE_PROJECTS_PERMISSION]: { text: learnMore, href: LEARN_MORE_URL },
	[NO_CREATE_ISSUES_PERMISSION]: { text: learnMore, href: LEARN_MORE_URL },
	[NO_TRANSITION_ISSUES_PERMISSION]: { text: learnMore, href: LEARN_MORE_URL },
	[NO_EDIT_ISSUES_PERMISSION]: { text: learnMore, href: LEARN_MORE_URL },
	[WATCHING_DISABLED]: { text: learnMore, href: LEARN_MORE_URL },
	[UNKNOWN_EXCEPTION]: { text: contactSupport, href: CONTACT_SUPPORT_URL },
};
